/**
 * What a pick of a list's item field builds (Feature 82 D9, D11): in a
 * condition, a list test reading any item, holding one item condition on
 * the field with its type's first operator; in a list input's mapping, a
 * list filter on the list handing on that field of every item. The
 * picker's own `[]` path never reaches the template, so nothing stored
 * reads a list by position.
 */
import type { LinkageListFilter, LinkageListTest, LinkageRule } from '@optum/api-client/types';

import { operatorsFor, VALUELESS } from './operators.js';
import type { ReferenceOption, Within } from './references.js';

/** A list's item field the picker offered, with the list it sits in. */
export type ItemPick = ReferenceOption & { within: Within };

/** The picked option when it is a list's item field; null for any other reference. */
export function itemPick(references: readonly ReferenceOption[], value: string): ItemPick | null {
  const option = references.find((r) => r.value === value);
  return option?.within ? (option as ItemPick) : null;
}

/** One condition on the item's field, with the first operator its type offers (D9). */
function itemRule(pick: ItemPick): LinkageRule {
  const op = operatorsFor(pick.type)[0] ?? 'exists';
  return VALUELESS.has(op)
    ? { ref: pick.within.item, op }
    : { ref: pick.within.item, op, value: '' };
}

/** The test a condition's pick of `result[].field` builds: any item where that field holds (D9). */
export function itemTest(pick: ItemPick): LinkageListTest {
  return {
    ref: pick.within.list,
    op: 'any_where',
    where: { join: 'all', items: [itemRule(pick)] },
  };
}

/** The filter a list input's pick builds: the list, handing on that field of every item (D11). */
export function itemFilter(pick: ItemPick): LinkageListFilter {
  return { ref: pick.within.list, pick: pick.within.item };
}
